'use client'

import { cn } from '@/lib/utils'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useCallback, useEffect, useState } from 'react'

import { SLIDES } from '../data/wcu-slides'

export function WhyChoseUs() {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 50 })

  const t = useTranslations('DigitalWallet')

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  const scrollTo = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  useEffect(() => {
    if (!emblaApi) return

    requestAnimationFrame(onSelect)
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return (
    <section className="mx-auto w-full max-w-245 min-w-0 shrink-0 grow-0 basis-full px-4">
      <h2 className="text-heading-5 md:text-heading-4 text-primary-600 mx-auto mb-6 max-w-105 text-center font-bold md:mb-11">
        {t('whyChoseUs.title')}
      </h2>
      <div className="relative w-full rounded-3xl bg-gray-50 px-4 pt-6 pb-4 md:rounded-4xl md:px-8 md:pb-8">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex">
            {SLIDES.map((slide, index) => (
              <div
                key={index}
                className={cn(
                  'flex h-full min-w-0 shrink-0 grow-0 basis-full flex-col-reverse gap-6 rounded-3xl px-2 md:flex-row md:items-center md:gap-28'
                )}
              >
                <div className="text-left">
                  <p className="text-heading-6 md:text-heading-5 text-primary-600 mb-3 font-bold">
                    {t(slide.title)}
                  </p>

                  <p className="text-body-3 text-primary-600 mb-4">
                    {t(slide.content)}
                  </p>
                </div>

                <Image
                  src={slide.image}
                  alt={t(slide.title)}
                  className="rounded-3xl object-cover md:ml-auto md:max-h-58.25 md:max-w-100"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-4 flex justify-center gap-2.5 md:mt-8">
        {SLIDES.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={cn(
              'h-2 cursor-pointer rounded-full transition-all duration-200',
              index === selectedIndex
                ? 'bg-secondary-500 w-7'
                : 'hover:bg-tertiary-700-400 w-3.5 bg-gray-300'
            )}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  )
}
